/**
 * Page Context - Provides the current page's entity hierarchy to the global AI assistant.
 *
 * Detail pages call `usePublishPageContext()` to announce which entity (and its parents)
 * is currently being viewed. The global AI button reads this via `usePageContext()` and
 * passes it to the backend so the AI knows what entity the user may be talking about.
 *
 * Example breadcrumb hierarchy for a task item:
 *   [
 *     { type: 'workspace', id: 'uuid', name: 'Engineering' },
 *     { type: 'board', id: 'uuid', name: 'Sprint 12' },
 *     { type: 'item', id: 'uuid', name: 'Fix login bug', description: '...' },
 *   ]
 */
import { createContext, useContext, useState, useMemo, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

/** A single entity in the page's breadcrumb hierarchy */
export interface PageEntityBreadcrumb {
  /** Entity type identifier (e.g., 'deal', 'board', 'item', 'ticket') */
  type: string;
  /** Entity UUID */
  id: string;
  /** Display name */
  name: string;
  /** Optional description / summary */
  description?: string | null;
}

/** Full page context published by detail pages */
export interface PageEntityContext {
  /** Module ID (e.g., 'tasks', 'sales', 'support') */
  module: string;
  /** Breadcrumb hierarchy from outermost parent to the focused entity */
  breadcrumbs: PageEntityBreadcrumb[];
}

interface PageContextValue {
  /** Current page entity context, or null if on a non-entity page */
  pageContext: PageEntityContext | null;
  /** Called by detail pages to publish their entity context */
  setPageContext: (context: PageEntityContext | null) => void;
}

const PageCtx = createContext<PageContextValue | undefined>(undefined);

export function PageContextProvider({ children }: { children: ReactNode }) {
  const [pageContext, setPageContext] = useState<PageEntityContext | null>(null);
  const value = useMemo(() => ({ pageContext, setPageContext }), [pageContext]);
  return <PageCtx.Provider value={value}>{children}</PageCtx.Provider>;
}

/**
 * Read the current page's entity context.
 * Used by GlobalAIButton to pass context to the AI assistant.
 */
export function usePageContext(): PageEntityContext | null {
  const ctx = useContext(PageCtx);
  if (!ctx) {
    throw new Error('usePageContext must be used within a PageContextProvider');
  }
  return ctx.pageContext;
}

/**
 * Publish the current page's entity hierarchy for the AI assistant.
 *
 * Call from detail pages with the entity data already loaded.
 * Automatically clears when the component unmounts (navigation away).
 *
 * @example
 * // In DealDetailPage:
 * usePublishPageContext('sales', [
 *   { type: 'deal', id: deal.id, name: deal.name, description: deal.description },
 * ]);
 *
 * // In TaskDetailPage with board context:
 * usePublishPageContext('tasks', [
 *   { type: 'board', id: board.id, name: board.name },
 *   { type: 'item', id: item.id, name: item.title, description: item.description },
 * ]);
 */
export function usePublishPageContext(
  module: string | null,
  breadcrumbs: PageEntityBreadcrumb[],
): void {
  const ctx = useContext(PageCtx);
  if (!ctx) {
    throw new Error('usePublishPageContext must be used within a PageContextProvider');
  }

  const { setPageContext } = ctx;

  // Stable serialized key to avoid unnecessary updates
  const serialized = useRef('');
  const newSerialized = module
    ? JSON.stringify({ module, breadcrumbs })
    : '';

  useEffect(() => {
    if (newSerialized !== serialized.current) {
      serialized.current = newSerialized;
      if (module && breadcrumbs.length > 0) {
        setPageContext({ module, breadcrumbs });
      } else {
        setPageContext(null);
      }
    }

    return () => {
      // Reset ref so the effect re-runs after StrictMode unmount/remount
      serialized.current = '';
      setPageContext(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- newSerialized captures module+breadcrumbs
  }, [newSerialized, setPageContext]);
}
